// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { isBundleReloadPending } from '@/hooks/use-app-version';
import { ZodError } from 'zod';

const MAX_RECEIVED_DATA_SAMPLE_LENGTH = 1000;

export interface SchemaValidationOptions {
  schemaName: string;
  endpoint: string;
  extra?: Record<string, unknown>;
}

/** A response that failed its schema, with its Sentry report already decided. */
export class SchemaValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SchemaValidationError';
  }
}

export function handleSchemaValidationError(
  error: ZodError,
  data: unknown,
  options: SchemaValidationOptions
): never {
  // Log the error in development
  if (import.meta.env.DEV) {
    console.error(
      `[Schema Validation Error] ${options.schemaName} at ${options.endpoint}`,
      {
        issues: error.issues,
        receivedData: data,
      }
    );
  }

  // A bundle awaiting its forced reload fails schemas by design
  if (!isBundleReloadPending()) {
    // Send the error to Sentry with context
    Sentry.captureException(error, {
      tags: {
        errorType: 'schema_validation',
        schemaName: options.schemaName,
        endpoint: options.endpoint,
      },
      extra: {
        ...options.extra,
        zodIssues: error.issues,
        // Include a sample of the received data (truncated for large responses)
        receivedDataSample: JSON.stringify(data).slice(
          0,
          MAX_RECEIVED_DATA_SAMPLE_LENGTH
        ),
      },
    });
  }

  // Create a user-facing error message
  // TODO(#15934): Handle this in more user-friendly way
  const fieldErrors = error.issues
    .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
    .join(', ');

  throw new SchemaValidationError(
    `Could not parse ${options.schemaName} response: ${fieldErrors}`
  );
}
